import React from 'react';
import { IntlProvider, useIntl } from 'react-intl';
import { Provider } from 'react-redux';
import { ApolloProvider } from '@apollo/client';
import { Query, Mutation } from '@apollo/client/react/components';
import gql from 'graphql-tag';
import JssProvider from 'react-jss/lib/JssProvider';
import { createGenerateClassName, jssPreset } from '@material-ui/core/styles';
import { create } from 'jss';
import { SnackbarProvider } from 'notistack';
import CssBaseline from '@material-ui/core/CssBaseline';
import { MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import client from '../lib/apolloClient';
import configureStore from '../store/findBobPublicStore';
import themes from '../lib/themes';
import getMessages from '../lib/i18n/getMessages';
import { CurrencyProvider } from '../hooks/useCurrency';
import MultiLineValuation, { ValuationEmptyState, ValuationCalculatingInterstitial, RevenueInformationModal } from '../components/Succession/MultiLineValuation';
import SuccessionThemeProvider from '../components/Succession/SuccessionThemeProvider';
import SingleColumnTemplate from '../../../ui/layouts/SingleColumnTemplate';
import GuidedTour from '../components/Shared/GuidedTour/GuidedTour';

// Tour steps for Multi-Line Valuation page
const getValuationTourSteps = (intl) => [
  {
    id: 'valuation-gauge',
    title: intl.formatMessage({ id: 'succession.multi_line_valuation.tour_gauge_title' }),
    description: intl.formatMessage({ id: 'succession.multi_line_valuation.tour_gauge_description' }),
    targetSelector: '[data-tutorial-id="valuation-gauge"]',
    position: 'bottom',
  },
  {
    id: 'methodology-tabs',
    title: intl.formatMessage({ id: 'succession.multi_line_valuation.tour_methodology_title' }),
    description: intl.formatMessage({ id: 'succession.multi_line_valuation.tour_methodology_description' }),
    targetSelector: '[data-tutorial-id="methodology-tabs"]',
    position: 'bottom',
  },
  {
    id: 'valuation-inputs',
    title: intl.formatMessage({ id: 'succession.multi_line_valuation.tour_inputs_title' }),
    description: intl.formatMessage({ id: 'succession.multi_line_valuation.tour_inputs_description' }),
    targetSelector: '[data-tutorial-id="valuation-inputs"]',
    position: 'left',
  },
  {
    id: 'save-export',
    title: intl.formatMessage({ id: 'succession.multi_line_valuation.tour_save_title' }),
    description: intl.formatMessage({ id: 'succession.multi_line_valuation.tour_save_description' }),
    targetSelector: '[data-tutorial-id="save-export"]',
    position: 'bottom',
  },
];

const MULTI_LINE_VALUATION_QUERY = gql`
  query MultiLineValuation($input: MultiLineValuationInput!) {
    multiLineValuation(input: $input) {
      combined
      combined_summary
      combined_summary_reported
      combined_summary_adjusted
      implied_multiples
      adjustment_impact
      summary_metrics
      weights
      features
      lines {
        line
        methods
        weighted
        adjusted
        factors
      }
    }
  }
`;

const RECALCULATE_MULTI_LINE_VALUATION = gql`
  mutation RecalculateMultiLineValuation($input: MultiLineValuationInput!) {
    recalculateMultiLineValuation(input: $input) {
      combined
      combined_summary
      combined_summary_reported
      combined_summary_adjusted
      implied_multiples
      adjustment_impact
      summary_metrics
      weights
      features
      lines {
        line
        methods
        weighted
        adjusted
        factors
      }
    }
  }
`;

const SAVE_MULTI_LINE_VALUATION = gql`
  mutation SaveMultiLineValuation($input: MultiLineValuationInput!) {
    saveMultiLineValuation(input: $input) {
      combined
      combined_summary
      combined_summary_reported
      combined_summary_adjusted
      implied_multiples
      adjustment_impact
      summary_metrics
      weights
      features
      vault_document_id
      vault_document_url
      lines {
        line
        methods
        weighted
        adjusted
        factors
      }
    }
  }
`;

const UPDATE_MULTI_LINE_VALUATION = gql`
  mutation UpdateMultiLineValuation($id: ID!, $input: MultiLineValuationInput!) {
    updateMultiLineValuation(id: $id, input: $input) {
      combined
      combined_summary
      combined_summary_reported
      combined_summary_adjusted
      implied_multiples
      adjustment_impact
      summary_metrics
      weights
      features
      vault_document_id
      vault_document_url
      lines {
        line
        methods
        weighted
        adjusted
        factors
      }
    }
  }
`;

const MULTI_LINE_SCENARIOS = gql`
  query MultiLineScenarios {
    multiLineScenarios {
      id
      name
      createdAt
      updatedAt
      results
      inputs
      expensesData
      methodsUsed
      author {
        id
        allowed_name
        profile_picture
        designations
      }
    }
  }
`;

const MULTI_LINE_SCENARIO = gql`
  query MultiLineScenario($id: ID!) {
    multiLineScenario(id: $id) {
      id
      name
      inputs
      results
      createdAt
    }
  }
`;

const GET_CURRENT_USER = gql`
  query getCurrentUserForMultiLine {
    getCurrentUser {
      id
      is_admin
      roles
      valuation_last_accessed_date
      gross_production
      profile_picture
      inventory_user { is_rjas }
      fee_based_revenue
      trailer_revenue
      direct_revenue
      transactional_revenue
      assets_under_management
      number_of_clients
      gross_expenses
      branch_net_payout
      qbo_synced_expenses
      expense_growth_rate
      year_1_gross
      year_2_gross
      year_3_gross
      year_4_gross
      year_5_gross
      li_first_year_commissions
      li_renewal_commissions
      li_override_commissions
      li_other_revenues
      li_policy_count
      life_year_1_gross
      life_year_2_gross
      life_year_3_gross
      life_year_4_gross
      life_year_5_gross
      pc_personal_lines_premium
      pc_personal_lines_commission_rate
      pc_commercial_lines_premium
      pc_commercial_lines_commission_rate
      pc_personal_retention_rate
      pc_commercial_retention_rate
      pc_year_1_commission
      pc_year_2_commission
      pc_year_3_commission
      pc_year_4_commission
      pc_year_5_commission
      group_first_year_commissions
      group_renewal_commissions
      group_override_commissions
      group_other_revenues
      group_number_of_cases
      group_average_case_size
      group_case_retention_rate
      group_year_1_gross
      group_year_2_gross
      group_year_3_gross
      group_year_4_gross
      group_year_5_gross
      company {
        id
        name
        country
        allow_user_to_specify_revenue_growth_rate
        imported_production_data_read_only
        feature_types
        wm_cost_of_equity_mid
        li_cost_of_equity_mid
        pc_cost_of_equity_mid
        group_cost_of_equity_mid
        rj_company_specific_risk
        rj_company_specific_risk_secondary
        rj_use_owners_comp_direct_expenses
        rjas_benchmark_expenses
        confidence_revenue_weight
        confidence_expenses_weight
        confidence_normalization_weight
        revenue_line_terminology
        multi_line_expense_categories {
          id
          key
          label
          description
          sortOrder
          groupName
          enabled
        }
      }
    }
    valuationValidation {
      is_validated
      validated_at
      validation_notes
      is_recent
      is_current_version
      validated_scenario_id
      coach_profile {
        id
        name
        headline
        avatar_url
        credentials
      }
      program_name
    }
  }
`;

const defaultLines = [
  { name: 'wealth' },
  { name: 'life' },
  { name: 'pc' },
  { name: 'group' },
];

const MultiLineValuationInner = ({ store, theme, variables, props, initialState }) => {
  const intl = useIntl();
  const generateClassName = createGenerateClassName({
    dangerouslyUseGlobalCSS: true,
    productionPrefix: 'fbSuccession',
  });

  const jss = create({ ...jssPreset() });

  return (
    <JssProvider jss={jss} generateClassName={generateClassName} classNamePrefix="fbSuccession-">
      <MuiThemeProvider theme={createMuiTheme(theme)}>
        <CssBaseline />
        <IntlProvider locale={locale} messages={getMessages(locale)}>
          <Provider store={store}>
            <SnackbarProvider maxSnack={5}>
              <ApolloProvider client={client}>
                <SuccessionThemeProvider company={initialState.company} user={props.current_user}>
                <Query query={GET_CURRENT_USER} fetchPolicy="network-only">
                  {({ data: userData }) => (
                <Query query={MULTI_LINE_VALUATION_QUERY} variables={variables} fetchPolicy="network-only">
                  {({ loading, error, data, refetch }) => (
                    <Query query={MULTI_LINE_SCENARIOS} fetchPolicy="network-only">
                      {({ data: scenariosData, refetch: refetchScenarios }) => (
                        <Mutation mutation={SAVE_MULTI_LINE_VALUATION}>
                          {(saveMutation, { loading: saving }) => (
                        <Mutation mutation={UPDATE_MULTI_LINE_VALUATION}>
                          {(updateMutation, { loading: updating }) => {
                            const [displayedResult, setDisplayedResult] = React.useState(null);
                            const [loadedScenarioId, setLoadedScenarioId] = React.useState(null);
                            const [loadedScenarioName, setLoadedScenarioName] = React.useState(null);
                            const deriveUserDefaults = (user, allowManualGrowth) => {
                              if (!user) return { wealth: {}, life: {}, pc: {}, group: {} };
                              const pct = (v) => {
                                const n = parseFloat(v);
                                if (!Number.isFinite(n)) return undefined;
                                return n > 1 ? n / 100 : n; // handle 15 -> 0.15
                              };
                              const num = (v) => {
                                const n = parseFloat(v);
                                return Number.isFinite(n) ? n : undefined;
                              };
                              const compactAvgGrowth = (arr) => {
                                const vals = (arr || []).map((v) => Number(v || 0)).filter((v) => Number.isFinite(v));
                                const cleaned = vals.filter((v) => v); // drop zeros
                                if (cleaned.length < 2) return undefined;
                                const growths = [];
                                for (let i = 0; i < cleaned.length - 1; i++) {
                                  const current = cleaned[i];
                                  const next = cleaned[i + 1];
                                  if (current && Number.isFinite(current) && Number.isFinite(next)) {
                                    growths.push((current - next) / current);
                                  }
                                }
                                if (!growths.length) return undefined;
                                // Divide by cleaned.length (# of data points) to match backend rj_gross_revenue_detail.rb
                                const avg = growths.reduce((s, v) => s + v, 0) / cleaned.length;
                                if (!Number.isFinite(avg)) return 0;
                                return avg;
                              };
                              // Historical series from stored per-line fields
                              const wealthHistory = [user.year_1_gross, user.year_2_gross, user.year_3_gross, user.year_4_gross, user.year_5_gross];
                              const lifeHistory = [user.life_year_1_gross, user.life_year_2_gross, user.life_year_3_gross, user.life_year_4_gross, user.life_year_5_gross];
                              const pcHistory = [user.pc_year_1_commission, user.pc_year_2_commission, user.pc_year_3_commission, user.pc_year_4_commission, user.pc_year_5_commission];
                              const groupHistory = [user.group_year_1_gross, user.group_year_2_gross, user.group_year_3_gross, user.group_year_4_gross, user.group_year_5_gross];
                              const autoGrowth = (historyArr) => (allowManualGrowth ? undefined : compactAvgGrowth(historyArr));
                              return {
                                wealth: {
                                  feeBasedRevenue: num(user.fee_based_revenue),
                                  trailCommissions: num(user.trailer_revenue),
                                  transactionalCommissions: num(user.transactional_revenue),
                                  otherDirectRevenue: num(user.direct_revenue),
                                  faRetentionPercent: pct(user.branch_net_payout),
                                  aum: num(user.assets_under_management),
                                  clientCount: num(user.number_of_clients),
                                  history: { y1: num(wealthHistory[0]), y2: num(wealthHistory[1]), y3: num(wealthHistory[2]), y4: num(wealthHistory[3]), y5: num(wealthHistory[4]) },
                                  growth: autoGrowth(wealthHistory),
                                },
                                life: {
                                  // Life Insurance inputs from User.rb (li_* fields)
                                  firstYearCommissions: num(user.li_first_year_commissions),
                                  renewalCommissions: num(user.li_renewal_commissions),
                                  overrideCommissions: num(user.li_override_commissions),
                                  otherRevenue: num(user.li_other_revenues),
                                  policyCount: num(user.li_policy_count),
                                  history: { y1: num(lifeHistory[0]), y2: num(lifeHistory[1]), y3: num(lifeHistory[2]), y4: num(lifeHistory[3]), y5: num(lifeHistory[4]) },
                                  growth: autoGrowth(lifeHistory),
                                },
                                pc: {
                                  // P&C inputs from User.rb (pc_* fields)
                                  personalPremium: num(user.pc_personal_lines_premium),
                                  personalCommRate: pct(user.pc_personal_lines_commission_rate),
                                  commercialPremium: num(user.pc_commercial_lines_premium),
                                  commercialCommRate: pct(user.pc_commercial_lines_commission_rate),
                                  retentionPersonal: pct(user.pc_personal_retention_rate),
                                  retentionCommercial: pct(user.pc_commercial_retention_rate),
                                  history: { y1: num(pcHistory[0]), y2: num(pcHistory[1]), y3: num(pcHistory[2]), y4: num(pcHistory[3]), y5: num(pcHistory[4]) },
                                  growth: autoGrowth(pcHistory),
                                },
                                group: {
                                  // Group Insurance inputs from User.rb (group_* fields)
                                  firstYearCommissions: num(user.group_first_year_commissions),
                                  renewalCommissions: num(user.group_renewal_commissions),
                                  overrideCommissions: num(user.group_override_commissions),
                                  otherRevenue: num(user.group_other_revenues),
                                  numberOfCases: num(user.group_number_of_cases),
                                  avgCaseSize: num(user.group_average_case_size),
                                  retentionRate: pct(user.group_case_retention_rate),
                                  history: { y1: num(groupHistory[0]), y2: num(groupHistory[1]), y3: num(groupHistory[2]), y4: num(groupHistory[3]), y5: num(groupHistory[4]) },
                                  growth: autoGrowth(groupHistory),
                                },
                              };
                            };
                            const [allowManualGrowth, setAllowManualGrowth] = React.useState(!!props?.current_user?.company?.allow_user_to_specify_revenue_growth_rate);
                            const [activeInputs, setActiveInputs] = React.useState(deriveUserDefaults(props.current_user, allowManualGrowth));
                            const [activeExpenses, setActiveExpenses] = React.useState(() => {
                              const qboExpenses = props?.current_user?.qbo_synced_expenses;
                              if (qboExpenses && typeof qboExpenses === 'object' && Object.keys(qboExpenses).length > 0) {
                                return {
                                  operatingExpenseCategories: Object.entries(qboExpenses).map(([key, amount]) => ({ key, amount })),
                                  useBenchmark: false,
                                };
                              }
                              return null;
                            });
                            const hasAnyValues = (obj) => obj && Object.values(obj).some((v) => v !== undefined && v !== null && v !== '');
                            React.useEffect(() => {
                              (async () => {
                                try {
                                  const resCU = await client.query({ query: GET_CURRENT_USER, fetchPolicy: 'network-only' });
                                  const cu = resCU?.data?.getCurrentUser;
                                  if (cu) {
                                    setAllowManualGrowth(!!cu?.company?.allow_user_to_specify_revenue_growth_rate);
                                    setActiveInputs(deriveUserDefaults(cu, !!cu?.company?.allow_user_to_specify_revenue_growth_rate));
                                    // Pre-populate expenses from QBO sync if no scenario is loaded.
                                    // useBenchmark: false so per-category inputs are visible and editable.
                                    if (cu?.qbo_synced_expenses && Object.keys(cu.qbo_synced_expenses).length > 0) {
                                      setActiveExpenses((prev) => prev || {
                                        operatingExpenseCategories: Object.entries(cu.qbo_synced_expenses).map(([key, amount]) => ({ key, amount })),
                                        useBenchmark: false,
                                      });
                                    }
                                  }
                                } catch (_e) {
                                  // ignore
                                }
                              })();
                              // eslint-disable-next-line react-hooks/exhaustive-deps
                            }, []);
                              const loadScenario = async (id) => {
                              const res = await client.query({ query: MULTI_LINE_SCENARIO, variables: { id }, fetchPolicy: 'network-only' });
                              const scenario = res?.data?.multiLineScenario;
                              
                              // Track the loaded scenario
                              setLoadedScenarioId(id);
                              setLoadedScenarioName(scenario?.name || null);
                              
                              // Option B: Point-in-time snapshot - show saved data as-is
                              // User must click "Recalculate" to get current rates
                              if (scenario?.inputs) {
                                const mapped = (scenario.inputs?.lines || []).reduce((acc, l) => {
                                  acc[l.name] = l.inputs || {};
                                  return acc;
                                }, { wealth: {}, life: {}, pc: {}, group: {} });
                                setActiveInputs((prev) => ({ ...prev, ...mapped }));
                                // Use expensesData first (dedicated field), fall back to inputs.expenses
                                const expenses = scenario.expensesData || scenario.expenses_data || scenario.inputs.expenses || null;
                                setActiveExpenses(expenses);
                              }
                              
                              // Show the saved results (point-in-time snapshot)
                              if (scenario?.results) {
                                setDisplayedResult(scenario.results);
                              }
                            };
                            const clearScenario = () => {
                              setDisplayedResult(null);
                              setActiveInputs(deriveUserDefaults(props.current_user));
                              const qboExpenses = props?.current_user?.qbo_synced_expenses;
                              if (qboExpenses && typeof qboExpenses === 'object' && Object.keys(qboExpenses).length > 0) {
                                setActiveExpenses({
                                  operatingExpenseCategories: Object.entries(qboExpenses).map(([key, amount]) => ({ key, amount })),
                                  useBenchmark: false,
                                });
                              } else {
                                setActiveExpenses(null);
                              }
                              setLoadedScenarioId(null);
                              setLoadedScenarioName(null);
                            };
                            // Check if user has never accessed valuation before
                            const viewerUser = userData?.getCurrentUser;
                            
                            // State for empty state flow
                            const [showRevenueModal, setShowRevenueModal] = React.useState(false);
                            const [showInterstitial, setShowInterstitial] = React.useState(false);
                            
                            // Determine if user has completed setup (accessed valuation before)
                            // Compute directly from data - don't use useState for this derived value
                            const isUserDataLoaded = userData !== undefined && viewerUser !== undefined;
                            const hasTimestamp = viewerUser?.valuation_last_accessed_date != null;
                            // Also check if user has revenue data (indicates they've used valuation before timestamp tracking was added)
                            const hasRevenueData = (viewerUser?.annual_revenue && viewerUser.annual_revenue > 0) ||
                                                   (viewerUser?.recurring_revenue && viewerUser.recurring_revenue > 0);
                            const hasAccessedValuation = hasTimestamp || hasRevenueData;

                            // Track if user completed setup during this session (for interstitial flow)
                            const [completedThisSession, setCompletedThisSession] = React.useState(false);

                            // Auto-stamp timestamp for users with data but missing timestamp
                            React.useEffect(() => {
                              if (!isUserDataLoaded || hasTimestamp || !hasRevenueData) return;
                              // User has revenue data but no timestamp - stamp it now
                              client.mutate({
                                mutation: RECALCULATE_MULTI_LINE_VALUATION,
                                variables: { input: { lines: defaultLines } },
                              }).catch(() => {});
                            }, [isUserDataLoaded, hasTimestamp, hasRevenueData]);

                            // Tour state
                            const [tourOpen, setTourOpen] = React.useState(false);
                            const userId = viewerUser?.id || props.current_user?.id || 'anonymous';
                            const tourStorageKey = `mlv_tour_shown_v1_${userId}`;
                            
                            // Auto-show tour on first visit (after valuation is displayed)
                            React.useEffect(() => {
                              if (!isUserDataLoaded || !hasAccessedValuation) return;
                              if (loading) return; // Wait for valuation to load
                              
                              try {
                                const params = new URLSearchParams(window.location.search || '');
                                const tourParam = params.get('tour');
                                
                                if (tourParam === 'reset') {
                                  localStorage.removeItem(tourStorageKey);
                                  setTimeout(() => setTourOpen(true), 500);
                                  return;
                                }
                                
                                if (tourParam === 'open') {
                                  setTimeout(() => setTourOpen(true), 500);
                                  try { localStorage.setItem(tourStorageKey, 'true'); } catch (_) {}
                                  return;
                                }
                                
                                // Auto-show on first visit
                                const hasSeenTour = localStorage.getItem(tourStorageKey) === 'true';
                                if (!hasSeenTour && !completedThisSession) {
                                  setTimeout(() => setTourOpen(true), 800);
                                  try { localStorage.setItem(tourStorageKey, 'true'); } catch (_) {}
                                }
                              } catch (_) {}
                            }, [isUserDataLoaded, hasAccessedValuation, loading, completedThisSession, tourStorageKey]);

                            const handleShowValuation = async () => {
                              setShowRevenueModal(false);
                              setShowInterstitial(true);
                              // Refetch user data first to get the newly saved revenue info
                              try {
                                const resCU = await client.query({ query: GET_CURRENT_USER, fetchPolicy: 'network-only' });
                                const cu = resCU?.data?.getCurrentUser;
                                if (cu) {
                                  // Update activeInputs with the fresh user data
                                  setAllowManualGrowth(!!cu?.company?.allow_user_to_specify_revenue_growth_rate);
                                  setActiveInputs(deriveUserDefaults(cu, !!cu?.company?.allow_user_to_specify_revenue_growth_rate));
                                }
                                // Now explicitly recalculate via mutation (this stamps valuation_last_accessed_date server-side)
                                const res = await client.mutate({
                                  mutation: RECALCULATE_MULTI_LINE_VALUATION,
                                  variables: { input: { lines: defaultLines } },
                                });
                                const nextVal = res?.data?.recalculateMultiLineValuation;
                                if (nextVal) setDisplayedResult(nextVal);
                              } catch (_e) {
                                // eslint-disable-next-line no-console
                                console.error('Failed to recalculate multi-line valuation after setup', _e);
                              }
                            };

                            const handleInterstitialComplete = () => {
                              setShowInterstitial(false);
                              setCompletedThisSession(true);
                            };

                            // Show interstitial overlay if in that state
                            if (showInterstitial) {
                              return (
                                <ValuationCalculatingInterstitial onComplete={handleInterstitialComplete} />
                              );
                            }

                            // Wait for user data to load before deciding what to show
                            if (!isUserDataLoaded) {
                              return <div className="p-8 text-center text-succession-text-secondary">{intl.formatMessage({ id: 'succession.multi_line_valuation.loading' })}</div>;
                            }

                            // Show empty state if user has never accessed valuation (and didn't just complete setup)
                            if (!hasAccessedValuation && !completedThisSession) {
                              return (
                                <>
                                  <SingleColumnTemplate title={intl.formatMessage({ id: 'succession.multi_line_valuation.business_valuation_title' })}>
                                    <ValuationEmptyState
                                      onSetupProfile={() => setShowRevenueModal(true)}
                                      isReadOnly={viewerUser?.company?.imported_production_data_read_only || false}
                                      companyName={viewerUser?.company?.name || 'your firm'}
                                      quickbooksConnectorEnabled={(props.features || []).includes('quickbooks_connector')}
                                      quickbooksConnected={props.quickbooks_connected || false}
                                      quickbooksConnectUrl={props.quickbooks_connect_url || null}
                                    />
                                  </SingleColumnTemplate>
                                  {showRevenueModal && (
                                    <RevenueInformationModal
                                      user={viewerUser || props.current_user}
                                      onClose={() => setShowRevenueModal(false)}
                                      onShowValuation={handleShowValuation}
                                      onError={(error) => console.error('Error saving revenue info:', error)}
                                      isReadOnly={viewerUser?.company?.imported_production_data_read_only || false}
                                      companyName={viewerUser?.company?.name || 'your firm'}
                                      quickbooksConnected={props.quickbooks_connected || false}
                                    />
                                  )}
                                </>
                              );
                            }

                            // Get validation data from user query
                            const validationData = userData?.valuationValidation;

                            return (
                              <>
                                <MultiLineValuation
                                  loading={loading}
                                  error={error}
                                  result={displayedResult || data?.multiLineValuation}
                                  scenarios={scenariosData?.multiLineScenarios || []}
                                  initialInputs={activeInputs}
                                  initialExpenses={activeExpenses}
                                  allowManualGrowth={allowManualGrowth}
                                  currentUser={props.current_user}
                                  viewerIsPrivileged={Boolean((viewerUser?.is_admin) || ((viewerUser?.roles || []).some((r)=> ['admin','customer','staff'].includes(String(r)))) || props.current_user?.admin || props.current_user?.customer || props.current_user?.staff)}
                                  viewerUser={viewerUser}
                                  terminology={viewerUser?.company?.revenue_line_terminology}
                                  validationData={validationData}
                                  loadedScenarioId={loadedScenarioId}
                                  loadedScenarioName={loadedScenarioName}
                                  saving={saving}
                                  updating={updating}
                                  onLoadScenario={loadScenario}
                                  onClearScenario={clearScenario}
                                  quickbooksConnectorEnabled={(props.features || []).includes('quickbooks_connector')}
                                  quickbooksConnected={props.quickbooks_connected || false}
                                  quickbooksConnectUrl={props.quickbooks_connect_url || null}
                                  quickbooksSyncUrl={props.quickbooks_sync_url || null}
                                  onRecalculate={async (lines) => {
                                    try {
                                      // Back-compat: older callers passed an array of lines; newer callers can pass { lines, expenses }
                                      const input = Array.isArray(lines)
                                        ? { lines }
                                        : (lines && typeof lines === 'object' && Array.isArray(lines.lines))
                                          ? { lines: lines.lines, ...(lines.expenses ? { expenses: lines.expenses } : {}) }
                                          : { lines: defaultLines };
                                      
                                      // Use mutation for recalculate to ensure fresh server calculation
                                      const res = await client.mutate({
                                        mutation: RECALCULATE_MULTI_LINE_VALUATION,
                                        variables: { input },
                                      });
                                      
                                      if (res?.data?.recalculateMultiLineValuation) {
                                        setDisplayedResult(res.data.recalculateMultiLineValuation);
                                      }
                                    } catch (_e) {
                                      // Fallback to refetch if mutation fails
                                      try {
                                        const input = Array.isArray(lines) ? { lines } : { lines: defaultLines };
                                        const res = await refetch({ input });
                                        if (res?.data?.multiLineValuation) {
                                          setDisplayedResult(res.data.multiLineValuation);
                                        }
                                      } catch (_e2) {
                                        // no-op
                                      }
                                    }
                                  }}
                                  onSave={async (name, payload) => {
                                    try {
                                      const inputPayload = payload.expenses 
                                        ? { lines: payload.lines, name, expenses: payload.expenses }
                                        : { lines: payload, name };
                                      const result = await saveMutation({ variables: { input: inputPayload } });
                                      setDisplayedResult(null);
                                      refetchScenarios && refetchScenarios();
                                      
                                      // Check if vault document was saved
                                      const vaultSaved = result?.data?.saveMultiLineValuation?.vault_document_id;
                                      return { success: true, vaultSaved: !!vaultSaved, name };
                                    } catch (error) {
                                      console.error('Save error:', error);
                                      return { success: false, error: error.message };
                                    }
                                  }}
                                  onUpdate={async (payload) => {
                                    if (!loadedScenarioId) return { success: false, error: 'No scenario loaded' };
                                    try {
                                      const inputPayload = payload.expenses 
                                        ? { lines: payload.lines, expenses: payload.expenses }
                                        : { lines: payload.lines || payload };
                                      const result = await updateMutation({ 
                                        variables: { 
                                          id: loadedScenarioId, 
                                          input: inputPayload 
                                        } 
                                      });
                                      if (result?.data?.updateMultiLineValuation) {
                                        setDisplayedResult(result.data.updateMultiLineValuation);
                                      }
                                      refetchScenarios && refetchScenarios();
                                      
                                      const vaultSaved = result?.data?.updateMultiLineValuation?.vault_document_id;
                                      return { success: true, vaultSaved: !!vaultSaved, name: loadedScenarioName };
                                    } catch (error) {
                                      console.error('Update error:', error);
                                      return { success: false, error: error.message };
                                    }
                                  }}
                                />
                                <GuidedTour
                                  isOpen={tourOpen}
                                  steps={getValuationTourSteps(intl)}
                                  onClose={() => setTourOpen(false)}
                                  onComplete={() => {
                                    setTourOpen(false);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                  }}
                                  onReplayRequest={() => setTourOpen(true)}
                                />
                              </>
                            );
                          }}
                        </Mutation>
                          )}
                        </Mutation>
                      )}
                    </Query>
                  )}
                </Query>
                  )}
                </Query>
                </SuccessionThemeProvider>
              </ApolloProvider>
            </SnackbarProvider>
          </Provider>
        </IntlProvider>
      </MuiThemeProvider>
    </JssProvider>
  );
};

const MultiLineValuationContainer = (props, railsContext) => {
  const initialState = {
    company: props.company || {
      host: railsContext.company_host,
      features: railsContext.features,
      file_import_disclaimer: railsContext.file_import_disclaimer,
      name: railsContext.company_name,
      branded_name: railsContext.company_branded_name,
      short_name: railsContext.company_short_name,
    },
    user: props.current_user,
    intl: { locale: railsContext.i18nLocale || 'en' },
  };

  const store = configureStore(initialState);
  const theme = themes[railsContext.theme_name];
  const variables = { input: { lines: defaultLines } };
  const locale = railsContext.i18nLocale || window.locale || 'en';
  window.currency = (railsContext && railsContext.currency) || window.currency || 'USD';

  return (
    <CurrencyProvider currency={(railsContext && railsContext.currency) || window.currency || 'USD'}>
      <IntlProvider locale={locale} messages={getMessages(locale)}>
        <MultiLineValuationInner
          store={store}
          theme={theme}
          variables={variables}
          props={props}
          initialState={initialState}
        />
      </IntlProvider>
    </CurrencyProvider>
  );
};

export default MultiLineValuationContainer;


